// Video Modal Component
class VideoModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.playerContainer = null;
  }

  connectedCallback() {
    this.innerHTML = `
      <dialog-element id="video-modal-${this.id}" class="video-modal-dialog">
        <div class="video-modal-content">
          <button is="close-button" class="video-modal-close" aria-label="Close video">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="video-modal-player" id="video-player-container-${this.id}">
            <!-- Video will be injected here -->
          </div>
        </div>
      </dialog-element>
    `;

    this.modal = this.querySelector(`#video-modal-${this.id}`);
    this.playerContainer = this.querySelector(
      `#video-player-container-${this.id}`
    );
    this.init();
  }

  init() {
    // Listen for video modal open events
    document.addEventListener("video:open", (event) => {
      this.openVideo(event.detail.videoId, event.detail.platform);
    });

    // Close modal when clicking outside content
    this.modal?.addEventListener("click", (event) => {
      if (event.target === this.modal) {
        this.closeVideo();
      }
    });

    // Close modal on escape key
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.modal?.hasAttribute("open")) {
        this.closeVideo();
      }
    });

    // Close modal when close button is clicked
    const closeButton = this.modal?.querySelector(".video-modal-close");
    closeButton?.addEventListener("click", () => {
      this.closeVideo();
    });
  }

  openVideo(videoId, platform = "youtube") {
    if (!videoId || !this.modal) return;

    let embedUrl;
    switch (platform) {
      case "youtube":
        embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
        break;
      case "vimeo":
        embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
        break;
      default:
        console.error("Unsupported video platform:", platform);
        return;
    }

    // Create iframe
    const iframe = document.createElement("iframe");
    iframe.src = embedUrl;
    iframe.allowFullscreen = true;
    iframe.allow = "autoplay; encrypted-media";

    // Clear previous content and add new iframe
    this.playerContainer.innerHTML = "";
    this.playerContainer.appendChild(iframe);

    // Show modal
    this.modal.show();

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  closeVideo() {
    if (!this.modal) return;

    // Hide modal
    this.modal.hide();

    // Clear video content to stop playback
    setTimeout(() => {
      this.playerContainer.innerHTML = "";
    }, 300);

    // Restore body scroll
    document.body.style.overflow = "";
  }
}

// Latest YouTube Videos Component
class LatestYoutubeVideos extends HTMLElement {
  constructor() {
    super();
    this.playlistId =
      this.getAttribute("playlist-id") || "UUDB48XknQ_lWdDF0LYifS3w";
    this.maxResults = parseInt(this.getAttribute("max-results")) || 12;
    this.apiKey = "AIzaSyCWDxIPhmDV1geGUe0zxGFmXbmz7vuXZQA";
    this.videoGrid = null;
    this.loadingIndicator = null;
  }

  connectedCallback() {
    this.innerHTML = `
      <div class="latest-youtube-videos">
        <div class="video-feeds swiper" id="video-feeds-${this.id}">
          <div class="swiper-wrapper" id="video-grid-${this.id}">
            <!-- Videos will be loaded here via JavaScript -->
          </div>
          <div class="swiper-pagination"></div>
        </div>
        <div class="loading-indicator" id="loading-${this.id}">
          <p>Loading videos...</p>
        </div>
      </div>
    `;

    this.videoGrid = this.querySelector(`#video-grid-${this.id}`);
    this.loadingIndicator = this.querySelector(`#loading-${this.id}`);

    // Load videos when component is connected
    this.loadYouTubeVideos();
  }

  async loadYouTubeVideos() {
    try {
      let allVideos = [];
      let nextPageToken = "";
      let videosCollected = 0;

      // Fetch videos with pagination support
      while (videosCollected < this.maxResults) {
        const remainingVideos = this.maxResults - videosCollected;
        const requestCount = Math.min(50, remainingVideos);

        let url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${this.playlistId}&maxResults=${requestCount}&key=${this.apiKey}`;

        if (nextPageToken) {
          url += `&pageToken=${nextPageToken}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (data.error) {
          console.error("YouTube API Error:", data.error);
          break;
        }

        allVideos = allVideos.concat(data.items);
        videosCollected += data.items.length;

        if (data.nextPageToken && videosCollected < this.maxResults) {
          nextPageToken = data.nextPageToken;
        } else {
          break;
        }
      }

      // Limit to exact number requested
      allVideos = allVideos.slice(0, this.maxResults);

      // Hide loading indicator
      this.loadingIndicator.classList.add("hidden");

      // Render videos
      this.renderVideos(allVideos);
    } catch (error) {
      console.error("Error loading YouTube videos:", error);
      this.loadingIndicator.innerHTML =
        "<p>Error loading videos. Please try again later.</p>";
    }
  }

  renderVideos(videos) {
    this.videoGrid.innerHTML = "";

    videos.forEach((item) => {
      const imgUrl =
        item?.snippet?.thumbnails?.maxres?.url ||
        item?.snippet?.thumbnails?.high?.url ||
        item?.snippet?.thumbnails?.medium?.url ||
        item?.snippet?.thumbnails?.default?.url;

      const videoElement = document.createElement("div");
      videoElement.className = "swiper-slide";
      videoElement.innerHTML = `
        <div class="video-container">
          <div class="img-wrapper">
            <img src="${imgUrl}" alt="${item.snippet.title}" loading="lazy" style="width: 100%;">
            <div class="play-button video-modal-trigger" 
                 data-video-id="${item.snippet.resourceId.videoId}"
                 data-platform="youtube"
                 aria-label="Play video: ${item.snippet.title}">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"
                   role="presentation" class="icon icon-play" fill="none" viewBox="0 0 10 14">
                <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M1.48177 0.814643C0.81532 0.448245 0 0.930414 0 1.69094V12.2081C0 12.991 0.858787 13.4702 1.52503 13.0592L10.5398 7.49813C11.1918 7.09588 11.1679 6.13985 10.4965 5.77075L1.48177 0.814643Z"
                      fill="#ffffff"></path>
              </svg>
            </div>
            <div class="video-info">
              <h4 class="video-title">${item.snippet.title}</h4>
            </div>
          </div>
        </div>
      `;

      this.videoGrid.appendChild(videoElement);
    });

    // Initialize video triggers
    this.initVideoTriggers();

    // Initialize Swiper after videos are loaded
    this.initSwiper();
  }

  initVideoTriggers() {
    const videoTriggers = this.querySelectorAll(".video-modal-trigger");

    videoTriggers.forEach((button) => {
      button.addEventListener("click", function (event) {
        event.preventDefault();
        const videoId = this.dataset.videoId;
        const platform = this.dataset.platform || "youtube";

        if (videoId) {
          document.dispatchEvent(
            new CustomEvent("video:open", {
              detail: {
                videoId: videoId,
                platform: platform,
              },
            })
          );
        }
      });
    });
  }

  initSwiper() {
    // Wait for Swiper to be available
    if (typeof Swiper !== "undefined") {
      const swiperElement = this.querySelector(".swiper");
      if (swiperElement) {
        new Swiper(swiperElement, {
          slidesPerView: 3,
          spaceBetween: 10,
          slidesPerGroup: 3,
          grid: {
            fill: "row",
            rows: 2,
          },
          pagination: {
            el: ".swiper-pagination",
            clickable: true,
          },
          breakpoints: {
            769: {
              slidesPerView: 5,
              slidesPerGroup: 4,
              spaceBetween: 30,
              grid: {
                fill: "row",
                rows: 2,
              },
            },
          },
        });
      }
    } else {
      // Retry after a short delay if Swiper is not loaded yet
      setTimeout(() => this.initSwiper(), 100);
    }
  }
}

// Register custom elements
if (!customElements.get("video-modal")) {
  customElements.define("video-modal", VideoModal);
}

if (!customElements.get("latest-youtube-videos")) {
  customElements.define("latest-youtube-videos", LatestYoutubeVideos);
}

// Auto-generate unique IDs for components
document.addEventListener("DOMContentLoaded", function () {
  let videoModalCounter = 0;
  let youtubeVideoCounter = 0;

  // Auto-assign IDs to video-modal components
  document.querySelectorAll("video-modal:not([id])").forEach((element) => {
    element.id = `video-modal-${++videoModalCounter}`;
  });

  // Auto-assign IDs to latest-youtube-videos components
  document
    .querySelectorAll("latest-youtube-videos:not([id])")
    .forEach((element) => {
      element.id = `youtube-videos-${++youtubeVideoCounter}`;
    });
});

// Add CSS styles for the components
const style = document.createElement("style");
style.textContent = `
  /* Video Modal Styles */
  .video-modal-dialog {
    --modal-max-width: min(90vw, 1200px);
    --modal-max-height: min(90vh, 675px);
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    padding: 2rem;
    box-sizing: border-box;
  }

  .video-modal-dialog[open] {
    opacity: 1;
    visibility: visible;
  }

  .video-modal-content {
    position: relative;
    background: #000;
    // border-radius: 8px;
    width: 100%;
    max-width: var(--modal-max-width);
    max-height: var(--modal-max-height);
    aspect-ratio: 16 / 9;
    overflow: visible;
    transform: scale(0.9);
    transition: transform 0.3s ease;
    margin: 3rem;
  }

  .video-modal-dialog[open] .video-modal-content {
    transform: scale(1);
  }

  .video-modal-close {
    position: absolute;
    top: -1.5rem;
    right: -2.2rem;
    background: rgba(0, 0, 0, 0.9);
    border: none;
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 10;
    padding: 0.75rem;
    border-radius: 50%;
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .video-modal-close:hover {
    background: rgba(255, 255, 255, 0.9);
    color: #000;
    transform: scale(1.1);
    border-color: rgba(0, 0, 0, 0.2);
  }

  .video-modal-close svg {
    width: 20px;
    height: 20px;
  }

  .video-modal-player {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .video-modal-player iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Latest YouTube Videos Styles */

  .video-feeds {
    margin-top: 20px;
    margin-bottom: 40px;
    padding-bottom: 45px;
  }

  .video-container {
    position: relative;
  }

  .img-wrapper {
    position: relative;
    // border-radius: 12px;
    overflow: hidden;
    background: #000;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .img-wrapper:hover {
    // transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  .img-wrapper:hover .video-info {
    opacity: 1;
  }

  .img-wrapper img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .img-wrapper:hover img {
    // transform: scale(1.05);
  }

  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 0, 0, 0.8);
    border: none;
    border-radius: 50%;
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 2;
  }

  .play-button:hover {
    background: rgba(255, 0, 0, 0.9);
    transform: translate(-50%, -50%) scale(1.1);
  }

  .play-button .icon-play {
    width: 20px;
    height: 20px;
    fill: white;
  }

  .video-info {
    opacity: 0;
    padding: 1.5rem;
    background: linear-gradient(to top, transparent, rgba(0, 0, 0, 0.8));
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    color: white;
    height: 100%;
    width: 100%;
    transition: all 0.3s ease;
  }

  .video-title {
    margin: 0;
    font-size: 1rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-align: center;
  }

  .loading-indicator {
    text-align: center;
    padding: 2rem;
    color: #666;
  }

  .loading-indicator.hidden {
    display: none;
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .video-modal-dialog {
      padding: 1rem;
    }

    .video-modal-content {
      margin: 2rem;
    }

    .video-modal-close {
      top: -1rem;
      right: -1rem;
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1rem;
    }

    .video-modal-close svg {
      width: 16px;
      height: 16px;
    }

    .play-button {
      width: 38px;
      height: 38px;
    }

    .play-button .icon-play {
      width: 15px;
      height: 15px;
    }

    .video-info {
      padding: 1rem;
    }

    .video-title {
      font-size: 1rem;
    }
  }
`;

document.head.appendChild(style);
